<template lang="pug">
  .width-100
    #media.column(v-if='hasMedia')
      img.media-picture.m-bottom-1(:src='triggerMedia' v-if='mediaIsNotVideo' :alt='resource.title')
      .media-presentation.m-bottom-3.width-100(v-else v-html='triggerMediaHtml')
    .text-small.black.m-bottom-2(v-if='mediaSummary' v-html='mediaSummary')
    .column
      .column#rhetoricalQuestion.m-bottom-2(v-if='showRhetoricalQuestion')
        h2.title-medium.m-bottom-1 {{ innerI18n.t('student.rhetoricalQuestion') }}
        h4.text-big.text-overflow {{ rhetoricalQuestion }}
      .column#classPlanDescription.m-bottom-2(v-if='showClassPlan')
        h3.title-medium.m-bottom-1
          | {{ isStudentTab ? innerI18n.t('student.classPlan') : innerI18n.t('teacher.classPlan') }}
        .md-content.m-left-1(v-if='classPlan' v-html='classPlan')
        .md-content.m-left-1(v-if='!classPlan && resourceClasses.length > 1')
          ul
            li(v-for='resClass in resourceClasses')
              router-link(:to='resClassLink(resClass)')
                | {{ getClassListItem(resClass) }}
      .column#pedagogicalGoal
        h3.title-medium.m-bottom-1(v-if='pedagogicalGoal')
          | {{ isStudentTab ? innerI18n.t('student.resourceObjective') : innerI18n.t('teacher.resourceObjective') }}
        .md-content.m-left-1(v-if='pedagogicalGoal' v-html='pedagogicalGoal')
</template>

<script>
import MarkdownIt from 'markdown-it'
import { mapState } from 'vuex'
import { innerI18n } from '../../main'
import { routes } from '../../router'
import { getOEmbedHtml, getOEmbedUrl } from '../../utils/embed'

export default {
  props: {
    sharedClasses: { type: Array }
  },
  data() {
    return {
      innerI18n,
      routes,
      md: new MarkdownIt({ html: true }),
      triggerMediaHtml: null
    }
  },
  watch: {
    async resource() {
      await this.loadTriggerVideo()
    }
  },
  computed: {
    ...mapState({
      resource: state => state.resource.resource
    }),
    isStudentView() {
      return this.$route.meta.isStudentPublicResource
    },
    showClassPlan() {
      return !this.isStudentTab && (this.classPlan || this.resourceClasses.length > 1)
    },
    showRhetoricalQuestion() {
      return (this.isStudentTab || this.isStudentView) && this.rhetoricalQuestion
    },
    hasMedia() {
      if (this.isStudentView) {
        return this.resource.hasMedia
      } else if (this.isStudentTab) {
        return this.resource.studentMaterial.hasMedia
      } else {
        return this.resource.teacherMaterial.hasMedia
      }
    },
    triggerMedia() {
      if (this.isStudentView) {
        return this.resource.media
      } else if (this.isStudentTab) {
        return this.resource.studentMaterial.media
      } else {
        return this.resource.teacherMaterial.media
      }
    },
    mediaIsNotVideo() {
      if (this.isStudentView) {
        return this.resource.mediaType !== 'Video'
      } else if (this.isStudentTab) {
        return this.resource.studentMaterial.mediaType !== 'Video'
      } else {
        return this.resource.teacherMaterial.mediaType !== 'Video'
      }
    },
    mediaSummary() {
      if (this.isStudentView) {
        return this.mdRender(this.resource, 'mediaSummary')
      } else if (this.isStudentTab) {
        return this.mdRender(this.resource.studentMaterial, 'mediaSummary')
      } else {
        return this.mdRender(this.resource.teacherMaterial, 'mediaSummary')
      }
    },
    isStudentTab() {
      return this.$route.name === routes.studentResourceTrigger
    },
    classPlan() {
      return this.mdRender(this.resource.teacherMaterial, 'classPlanDescription')
    },
    pedagogicalGoal() {
      if (this.isStudentView) return this.mdRender(this.resource, 'studentPedagogicalGoal')
      if (this.isStudentTab) return this.mdRender(this.resource.studentMaterial, 'studentPedagogicalGoal')
      return this.mdRender(this.resource.teacherMaterial, 'pedagogicalGoal')
    },
    rhetoricalQuestion() {
      if (this.isStudentView) return this.resource.rhetoricalQuestion
      else return this.resource.presentation.rhetoricalQuestion
    },
    resourceClasses() {
      if (this.isStudentView) {
        return this.resource.classPlans
          .filter(resClass => !this.sharedClasses.length || this.sharedClasses.includes(resClass.classNumber))
          .map(resClass => resClass)
          .sort((a, b) => a.classNumber - b.classNumber)
      } else {
        return this.resource.teacherMaterial.classPlans
          .map(resClass => resClass)
          .sort((a, b) => a.classNumber - b.classNumber)
      }
    }
  },
  async created() {
    if (!this.mediaIsNotVideo) await this.loadTriggerVideo()
  },
  methods: {
    async loadTriggerVideo() {
      let media
      if (this.isStudentView) {
        media = this.resource.media
      } else if (this.isStudentTab) {
        media = this.resource.studentMaterial.media
      } else {
        media = this.resource.teacherMaterial.media
      }
      const oembedUrl = getOEmbedUrl(media)
      const html = await getOEmbedHtml(oembedUrl)
      this.triggerMediaHtml = html
    },
    resClassLink(resClass) {
      return this.isStudentView
        ? { name: routes.studentPublicResourceClass, params: { classId: resClass.classNumber } }
        : { name: routes.teacherResourceClass, params: { classId: resClass.classNumber } }
    },
    getClassListItem(resClass) {
      return resClass.name
        ? `${innerI18n.t('sidebar.class')} ${resClass.classNumber}: ${resClass.name}`
        : `${innerI18n.t('sidebar.class')} ${resClass.classNumber}`
    },
    mdRender(state, field) {
      return state && state[field] ? this.md.render(state[field]) : null
    }
  }
}
</script>

<style lang="scss" scoped>

.media-presentation {
  border: 0;
  width: 100%;
  height: 400px;
}

.media-picture {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
}
</style>
